/**
 * Where to walk on the current planet: commanded spots, objectives,
 * hiding from the enemies that can see you, or personality-driven sampling.
 */

import { dtan } from '../../core/dmath';
import { nearestSurfaceVertex } from '../../core/surface';
import { knownEnemies } from './targeting';
import { coverAround, lowestSpotsWithin } from '../../core/cover';
import type { PlayerState, GameState, BotConfig, BotFogContext, Vec3 } from '../../types';
import type { BotMoveResult, BotExposureContext } from './types';
import { vecSub, vecNormalize, vecDot, vecCross, vecScale, vecAdd, vec3 } from '../../math';
import type { SeededRandom } from '../../core/random';
import { eyePointAt } from '../../core/los';
import { getBotObjectiveTarget, getBotSamePlanetObjectiveDir } from '../botModeAI';
import { getPlayerWorldPos, getAliveEnemies, generateCandidateDirections, countEnemyLOS } from './common';
import { tuningFor } from './tuning';
import { dcos, dsin } from '../../core/dmath';

/**
 * Compute a walk target on the current planet.
 * With exposure context, spotted bots walk toward the antipodal point
 * (hiding behind the planet body from enemy LOS).
 */
export function computeBotMove(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  rng: SeededRandom,
  fogContext?: BotFogContext,
  exposure?: BotExposureContext
): BotMoveResult | null {
  const planet = gameState.planets[player.planetIndex];
  // K3: only the enemies the team knows about (visible, or where their ghosts stand) — 37.20 ledger #2
  const enemies = knownEnemies(player, gameState, fogContext);
  const { personality } = config;
  const t = tuningFor(config).movement;

  // Command override: holdPosition → walk to commanded surfaceDir
  const cmd = player.pendingCommand;
  if (cmd?.type === 'holdPosition' && cmd.surfaceDir) {
    return { targetDir: vecNormalize(cmd.surfaceDir) };
  }

  // Same-planet objective: walk toward core/collectible/artifact on this planet
  // Priority over combat repositioning — the bot jumped here for a reason
  const samePlanetObj = getBotSamePlanetObjectiveDir(player, gameState);
  if (samePlanetObj) {
    return { targetDir: vecNormalize(samePlanetObj.surfaceDir) };
  }

  // Off-planet objective? Walk toward the side of the planet facing the target planet
  // (improves jump angle for next turn if we can't jump from current position)
  {
    const objTarget = getBotObjectiveTarget(player, gameState);
    if (objTarget && objTarget.planetIndex !== player.planetIndex) {
      const targetPlanet = gameState.planets[objTarget.planetIndex];
      const toTarget = vecSub(targetPlanet.position, planet.position);
      // Project onto planet surface: direction from planet center toward target planet
      return { targetDir: vecNormalize(toTarget) };
    }
  }

  // Exposure-aware: if spotted, walk toward antipodal point from average enemy direction
  if (exposure?.isSpotted && fogContext) {
    // Gather visible enemy positions + ghost positions
    const enemyDirs: Vec3[] = [];
    for (const id of fogContext.visibleEnemyIds) {
      const enemy = gameState.players.find(p => p.id === id && p.alive);
      if (enemy) {
        const enemyPos = getPlayerWorldPos(enemy, gameState.planets);
        const toEnemy = vecSub(enemyPos, planet.position);
        enemyDirs.push(vecNormalize(toEnemy));
      }
    }
    for (const ghost of fogContext.ghosts) {
      const toGhost = vecSub(ghost.position, planet.position);
      enemyDirs.push(vecNormalize(toGhost));
    }

    if (enemyDirs.length > 0) {
      // Average enemy direction from planet center
      let avgX = 0, avgY = 0, avgZ = 0;
      for (const d of enemyDirs) { avgX += d.x; avgY += d.y; avgZ += d.z; }
      const avgDir = vecNormalize(vec3(avgX / enemyDirs.length, avgY / enemyDirs.length, avgZ / enemyDirs.length));

      // Antipodal direction = opposite of average enemy direction on the planet surface
      const antipodalDir = vecNormalize(vec3(-avgDir.x, -avgDir.y, -avgDir.z));

      return { targetDir: antipodalDir };
    }
  }

  // Find enemies on same planet
  const samePlanetEnemies = enemies.filter(e => e.planetIndex === player.planetIndex);

  if (samePlanetEnemies.length > 0) {
    const nearestEnemy = samePlanetEnemies[0];
    const enemyDir = vecNormalize(nearestEnemy.surfaceDir);

    if (personality.aggression > t.closeInAbove) {
      // Walk toward enemy
      return { targetDir: enemyDir };
    } else {
      // Walk away from enemy
      const awayDir = vecNormalize(vecSub(
        vecScale(player.surfaceDir, 2),
        enemyDir
      ));
      return { targetDir: awayDir };
    }
  }

  // Personality-driven position sampling (no same-planet enemies)
  const { skill } = config;
  const candidateCount = t.candidateCount;
  const candidates = generateCandidateDirections(player.surfaceDir, candidateCount, t.candidateOffset);
  // 37.18c: a bot that values cover also considers the lowest spots it can reach this turn — the craters
  if (t.coverWeight > 0) candidates.push(...lowestSpotsWithin(planet, player.surfaceDir, Math.min(player.movementRemaining, 4)));

  if (personality.riskTolerance > t.chaoticMinRisk) {
    // Chaotic: pick a random candidate
    return { targetDir: candidates[rng.int(0, candidates.length - 1)] };
  }

  const defensive = personality.aggression < t.defensiveBelow;
  const aggressive = personality.aggression > t.aggressiveAbove;
  if (enemies.length > 0 && (defensive || aggressive || t.coverWeight > 0)) {
    // Score candidates by enemy LOS count — and, when the bot values it, by the cover the spot offers (37.18)
    let bestDir = candidates[0];
    let bestScore = -Infinity;

    for (const candidateDir of candidates) {
      const worldPos = eyePointAt(planet, candidateDir);
      const losCount = countEnemyLOS(worldPos, player.planetIndex, enemies, gameState.planets);

      // Defensive minimizes LOS, aggressive maximizes, balanced only cares about cover
      let score: number;
      if (defensive) {
        score = -losCount; // fewer enemies seeing = better
      } else if (aggressive) {
        score = losCount; // more enemies visible = better firing angles
      } else {
        score = 0;
      }
      if (t.coverWeight > 0) score += t.coverWeight * coverAround(planet, candidateDir);

      // Skill-scaled noise so lower-skill bots pick suboptimally
      score += (rng.next() - 0.5) * (1 - skill) * t.losNoise;

      if (score > bestScore) {
        bestScore = score;
        bestDir = candidateDir;
      }
    }

    return { targetDir: bestDir };
  }

  // Small random reposition (balanced personality fallback)
  const currentDir = vecNormalize(player.surfaceDir);
  const angle = rng.range(0, Math.PI * 2);
  const offset = rng.range(t.repositionMin, t.repositionMax);

  // Create a small offset direction
  let ref: Vec3 = { x: 1, y: 0, z: 0 };
  if (Math.abs(vecDot(currentDir, ref)) > 0.9) ref = { x: 0, y: 1, z: 0 };
  const tx = vecNormalize(vecCross(currentDir, ref));
  const ty = vecNormalize(vecCross(currentDir, tx));

  const targetDir = vecNormalize(vecAdd(
    currentDir,
    vecAdd(
      vecScale(tx, dcos(angle) * offset),
      vecScale(ty, dsin(angle) * offset)
    )
  ));

  return { targetDir };
}

/**
 * With nobody known: walk toward the ground the team has seen least recently — the candidate spot at this turn's
 * reach whose surroundings are stalest in team fog (never revealed counts most). Null when everything near is fresh
 * or there is no fog.
 */
export function exploreMove(player: PlayerState, gameState: GameState): BotMoveResult | null {
  const planet = gameState.planets[player.planetIndex];
  if (!planet || player.movementRemaining < 0.5) return null;
  const mates = gameState.players.map((p, i) => ({ p, i })).filter(x => x.p.teamIndex === player.teamIndex && gameState.fog?.[x.i]?.planets[player.planetIndex]);
  if (mates.length === 0) return null;
  // how stale a spot is to the team: never revealed counts most, then turns since the freshest teammate saw it
  const turn = gameState.turnNumber;
  const staleness = (v: number): number => {
    let freshest = -1;
    for (const m of mates) {
      const fp = gameState.fog[m.i].planets[player.planetIndex];
      if (fp.revealedVertices[v]) freshest = Math.max(freshest, fp.revealTurn[v]);
    }
    return freshest < 0 ? 10 : Math.min(10, turn - freshest);
  };
  const reach = Math.min(player.movementRemaining, 4) / Math.max(1, planet.radius);
  let best: { dir: Vec3; score: number } | null = null;
  for (const dir of generateCandidateDirections(player.surfaceDir, 8, dtan(reach))) {
    let score = 0;
    for (const probe of generateCandidateDirections(dir, 6, dtan(reach * 0.5))) score += staleness(nearestSurfaceVertex(planet, probe));
    score += 2 * staleness(nearestSurfaceVertex(planet, dir));
    if (!best || score > best.score) best = { dir, score };
  }
  return best && best.score > 0 ? { targetDir: vecNormalize(best.dir) } : null;
}
